import { useLeagueLogo } from "../data/LeagueEnvironmentProvider";
import { seasonTypeLabel } from "./utils";
import "./teamReportDesign.scss";

export interface PrintTOCEntry {
  /** Anchor id of the page this entry links to (PDF-clickable) */
  anchorId: string;
  code: string;
  title: string;
  /** Sub-item labels listed under the entry (not linked — same page) */
  subs: string[];
}

export interface PrintTOCPlayer {
  anchorId: string;
  name: string;
}

const seasonLabel = (season?: number) =>
  season ? `${season}-${String((season + 1) % 100).padStart(2, "0")}` : "";

/**
 * The 1G print-only table of contents page. Every main row (and player
 * name) is an internal anchor link — Chrome's print-to-PDF preserves those
 * as clickable PDF links. Page numbers are deliberately absent: real print
 * pagination isn't knowable from CSS (user call 2026-08-28).
 */
export default function PrintTOC({
  teamId,
  teamName,
  teamNickname,
  teamAbbrev,
  season,
  seasonType,
  gamesPlayed,
  record,
  asOfDate,
  entries,
  players,
  playersTitle = "Player Pages",
}: {
  teamId: number;
  teamName: string;
  teamNickname: string;
  teamAbbrev: string;
  season?: number;
  seasonType?: string;
  gamesPlayed?: number;
  record?: string;
  asOfDate?: string;
  entries: PrintTOCEntry[];
  players: PrintTOCPlayer[];
  /** The roster preview names its players' season and roster (they are not the report season's) */
  playersTitle?: string;
}) {
  const getTeamLogoUrl = useLeagueLogo();
  const teamCity = teamName.replace(teamNickname, "").trim();
  const scope = [
    seasonTypeLabel(seasonType),
    gamesPlayed !== undefined ? `${gamesPlayed} GP` : undefined,
    record,
    asOfDate ? `Data through ${asOfDate}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
  const playersCode = String(entries.length + 1).padStart(2, "0");

  return (
    <div
      className="trd toc trd-printonly"
      style={{ pageBreakAfter: "always", minHeight: "10in" }}
    >
      <div className="ph">
        <div className="rpt">
          {seasonLabel(season)} Team Report
          {asOfDate && <span className="yr"> · As of {asOfDate}</span>}
        </div>
        <div className="who">{teamName} · Contents</div>
      </div>
      <img className="toc-wm" src={getTeamLogoUrl(teamId)} alt="" />
      <div className="toc-id" style={{ margin: "0.28in 0 0.26in" }}>
        <div className="city">{teamCity}</div>
        <div className="nm">{teamNickname}</div>
        <div className="rpt2">{seasonLabel(season)} Team Report</div>
        {scope && <div className="scope2">{scope}</div>}
      </div>
      <div className="grp">
        <span className="k">Contents</span>
        <span className="bar" />
      </div>
      {entries.map((entry) => (
        <div key={entry.anchorId}>
          <a className="trow" href={`#${entry.anchorId}`}>
            <span className="code">{entry.code}</span>
            <span className="ttl">{entry.title}</span>
          </a>
          {entry.subs.length > 0 && (
            <div className="subs">
              {entry.subs.map((sub) => (
                <span key={sub}>{sub}</span>
              ))}
            </div>
          )}
        </div>
      ))}
      {players.length > 0 && (
        <div>
          <a className="trow" href={`#${players[0].anchorId}`}>
            <span className="code">{playersCode}</span>
            <span className="ttl">{playersTitle}</span>
          </a>
          <div className="subs c3">
            {players.map((player) => (
              <a key={player.anchorId} href={`#${player.anchorId}`}>
                {player.name}
              </a>
            ))}
          </div>
        </div>
      )}
      <div className="pf">
        <span>Portland Trail Blazers · Coaching Analytics · Internal</span>
        <span>
          Team Report · {teamAbbrev} · Contents
        </span>
      </div>
    </div>
  );
}
